import React, { useState, useEffect } from 'react';
import { api } from '../api/client.js';
import { copyToClipboard } from '../utils/clipboard.js';
import {
  Plus,
  Search,
  ExternalLink,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  RefreshCw,
  Send,
  ShieldCheck,
  HelpCircle,
  Activity,
  Laptop,
  Terminal,
  Lock,
  Unlock,
  ShieldAlert,
  Smartphone,
  Radio,
  Wifi,
  Download,
  History,
  QrCode,
} from 'lucide-react';

export const AdminPage: React.FC<{ onOpenShopDashboard: (shopId: string) => void }> = ({
  onOpenShopDashboard,
}) => {
  const [shops, setShops] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedShop, setSelectedShop] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'subscription' | 'whatsapp' | 'switches' | 'access' | 'agent' | 'danger'>('subscription');

  // Main Admin Views: shops, browser device approvals, agent deployment
  const [adminViewTab, setAdminViewTab] = useState<'shops' | 'devices' | 'pairing'>('shops');
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDeviceShopFilter, setSelectedDeviceShopFilter] = useState<string>('all');
  const [deviceSearchQuery, setDeviceSearchQuery] = useState<string>('');
  const [pairingModalShop, setPairingModalShop] = useState<any | null>(null);
  const [pairingData, setPairingData] = useState<any | null>(null);
  const [loadingPairing, setLoadingPairing] = useState(false);

  // Add Shop Form State (Username + Password, No Email required)
  const [newShop, setNewShop] = useState({
    name: '',
    phone: '',
    durationUnit: 'months' as 'days' | 'months',
    durationValue: 1,
    subscriptionMonths: 1,
    shopkeeperUsername: '',
    shopkeeperPassword: '',
    shopkeeperEmail: '',
    allowedIps: '',
    ipLockEnabled: false,
  });
  const [creationResult, setCreationResult] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // WhatsApp Credentials Form
  const [waCreds, setWaCreds] = useState({
    phoneNumberId: '',
    wabaId: '',
    verifyToken: '',
    appSecret: '',
    accessToken: '',
  });
  const [testingWa, setTestingWa] = useState(false);
  const [waTestResult, setWaTestResult] = useState<any | null>(null);
  const [waSaveResult, setWaSaveResult] = useState<any | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // WhatsApp Web Multi-Device (Option 2 - 100% Free Forever)
  const [baileysState, setBaileysState] = useState<{
    status: 'DISCONNECTED' | 'SCAN_QR' | 'CONNECTING' | 'CONNECTED';
    qrDataUrl?: string | null;
    phone?: string | null;
    connectedAt?: string | null;
    lastError?: string | null;
  } | null>(null);
  const [loadingBaileys, setLoadingBaileys] = useState(false);
  const [showMetaAccordion, setShowMetaAccordion] = useState(false);

  const loadBaileysStatus = async (shopId: string) => {
    try {
      const data = await api.getBaileysStatus(shopId);
      setBaileysState(data);
    } catch {}
  };

  const handleStartBaileys = async (shopId: string, force: boolean = false) => {
    setLoadingBaileys(true);
    try {
      const data = await api.startBaileys(shopId, force);
      setBaileysState(data);
    } catch (err: any) {
      alert(err.message || 'Failed to start WhatsApp pairing');
    } finally {
      setLoadingBaileys(false);
    }
  };

  const handleDisconnectBaileys = async (shopId: string) => {
    if (!confirm('Are you sure you want to unlink WhatsApp from this shop?')) return;
    setLoadingBaileys(true);
    try {
      await api.disconnectBaileys(shopId);
      await loadBaileysStatus(shopId);
      loadShops();
    } catch (err: any) {
      alert(err.message || 'Failed to disconnect WhatsApp');
    } finally {
      setLoadingBaileys(false);
    }
  };

  useEffect(() => {
    const handleQr = (e: any) => {
      const detail = e.detail;
      setBaileysState((prev) => ({
        ...prev,
        status: 'SCAN_QR',
        qrDataUrl: detail.qrDataUrl,
      }));
    };
    const handleStatus = (e: any) => {
      const detail = e.detail;
      setBaileysState((prev) => ({
        ...prev,
        status: detail.status,
        phone: detail.phone,
      }));
      loadShops();
    };

    window.addEventListener('baileys_qr', handleQr);
    window.addEventListener('baileys_status', handleStatus);
    return () => {
      window.removeEventListener('baileys_qr', handleQr);
      window.removeEventListener('baileys_status', handleStatus);
    };
  }, []);

  const loadWhatsAppCreds = async (shopId: string) => {
    try {
      const data = await api.getWhatsAppCredentials(shopId);
      if (data && data.configured) {
        setWaCreds({
          phoneNumberId: data.phoneNumberId || '',
          wabaId: data.wabaId || '',
          verifyToken: data.verifyToken || '',
          appSecret: data.appSecret || '',
          accessToken: data.accessTokenMasked || '',
        });
      } else {
        setWaCreds({
          phoneNumberId: '',
          wabaId: '',
          verifyToken: `pb_verify_${shopId.slice(0, 8)}`,
          appSecret: '',
          accessToken: '',
        });
      }
    } catch {
      // Fallback
    }
  };

  useEffect(() => {
    if (selectedShop && activeTab === 'whatsapp') {
      loadWhatsAppCreds(selectedShop.id);
      loadBaileysStatus(selectedShop.id);
    }
  }, [selectedShop?.id, activeTab]);

  // Shopkeeper Credentials Edit State in Modal
  const [shopkeeperCreds, setShopkeeperCreds] = useState<any | null>(null);
  const [loadingShopkeeperCreds, setLoadingShopkeeperCreds] = useState(false);
  const [editShopkeeperUsername, setEditShopkeeperUsername] = useState('');
  const [editShopkeeperEmail, setEditShopkeeperEmail] = useState('');
  const [editShopkeeperPassword, setEditShopkeeperPassword] = useState('');
  const [shopkeeperUpdateStatus, setShopkeeperUpdateStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [savingShopkeeperCreds, setSavingShopkeeperCreds] = useState(false);

  const loadShopkeeperCreds = async (shopId: string) => {
    setLoadingShopkeeperCreds(true);
    setShopkeeperUpdateStatus(null);
    try {
      const res = await api.getShopkeeperCredentials(shopId);
      setShopkeeperCreds(res);
      setEditShopkeeperUsername(res.username || '');
      const isInternalPlaceholder = res.email && (res.email.endsWith('.local.printbridge') || res.email.endsWith('.internal'));
      setEditShopkeeperEmail(isInternalPlaceholder ? '' : (res.email || ''));
      setEditShopkeeperPassword('');
    } catch {
      setShopkeeperCreds(null);
    } finally {
      setLoadingShopkeeperCreds(false);
    }
  };

  const handleSaveShopkeeperCreds = async (shopId: string) => {
    setSavingShopkeeperCreds(true);
    setShopkeeperUpdateStatus(null);
    try {
      await api.updateShopkeeperCredentials(shopId, {
        username: editShopkeeperUsername || undefined,
        email: editShopkeeperEmail,
        password: editShopkeeperPassword || undefined,
      });
      setShopkeeperUpdateStatus({ ok: true, msg: 'Shopkeeper credentials & email updated successfully!' });
      await loadShopkeeperCreds(shopId);
    } catch (err: any) {
      setShopkeeperUpdateStatus({ ok: false, msg: err.message || 'Failed to update credentials' });
    } finally {
      setSavingShopkeeperCreds(false);
    }
  };

  // Shop Dedicated IP Lock State in Modal
  const [shopIpLock, setShopIpLock] = useState({
    ipLockEnabled: false,
    allowedIps: '',
  });
  const [savingIpLock, setSavingIpLock] = useState(false);
  const [ipLockStatus, setIpLockStatus] = useState<string | null>(null);

  const handleSaveIpLock = async (shopId: string) => {
    setSavingIpLock(true);
    setIpLockStatus(null);
    try {
      const allowedArr = shopIpLock.allowedIps
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await api.updateShop(shopId, {
        ipLockEnabled: shopIpLock.ipLockEnabled,
        allowedIps: allowedArr,
      });
      setIpLockStatus('Shop IP lock & network policy updated successfully!');
      await loadShops();
      const updated = await api.getShop(shopId);
      setSelectedShop(updated);
    } catch (err: any) {
      setIpLockStatus(err.message || 'Failed to update IP policy');
    } finally {
      setSavingIpLock(false);
    }
  };

  // Shop-specific Devices State in Modal
  const [shopModalDevices, setShopModalDevices] = useState<any[]>([]);
  const [loadingShopModalDevices, setLoadingShopModalDevices] = useState(false);

  const loadShopModalDevices = async (shopId: string) => {
    setLoadingShopModalDevices(true);
    try {
      const list = await api.getShopDevices(shopId);
      setShopModalDevices(list || []);
    } catch {
      setShopModalDevices([]);
    } finally {
      setLoadingShopModalDevices(false);
    }
  };

  const handleToggleShopModalDeviceApproval = async (deviceId: string, approved: boolean, shopId: string) => {
    try {
      await api.toggleDeviceApproval(deviceId, approved);
      await loadShopModalDevices(shopId);
      await loadDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to update device authorization');
    }
  };

  const handleDeleteShopModalDevice = async (deviceId: string, shopId: string) => {
    if (!window.confirm('Are you sure you want to permanently revoke and delete this browser approval record? The device will be logged out immediately.')) {
      return;
    }
    try {
      // Step 1: Explicitly revoke first
      await api.toggleDeviceApproval(deviceId, false).catch(() => {});
      // Step 2: Permanently delete
      await api.deleteDevice(deviceId);
      await loadShopModalDevices(shopId);
      await loadDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to delete browser device approval');
    }
  };

  const handleCopy = async (text: string, key: string) => {
    await copyToClipboard(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const loadShops = async () => {
    try {
      const res = await api.getShops();
      setShops(res);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const list = await api.getAllDevices();
      const sorted = [...(list || [])].sort((a, b) => {
        const dateA = new Date(a.approved_at || a.created_at || 0).getTime();
        const dateB = new Date(b.approved_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });
      setDevices(sorted);
    } catch (err) {
      console.error('Failed to load devices', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    loadShops();
    loadDevices();
    const interval = setInterval(() => {
      loadShops();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Keep selectedShop live-synced with newest shop state
  useEffect(() => {
    if (selectedShop) {
      const fresh = shops.find((s) => s.id === selectedShop.id);
      if (fresh) {
        setSelectedShop(fresh);
      }
    }
  }, [shops]);

  const handleToggleDeviceApproval = async (deviceId: string, approved: boolean) => {
    try {
      await api.toggleDeviceApproval(deviceId, approved);
      await loadDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to update browser device authorization');
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to permanently revoke and delete this browser approval record? The device will be logged out immediately.')) {
      return;
    }
    try {
      // Step 1: Explicitly revoke first
      await api.toggleDeviceApproval(deviceId, false).catch(() => {});
      // Step 2: Permanently delete
      await api.deleteDevice(deviceId);
      await loadDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to delete browser device approval');
    }
  };

  const handleOpenPairing = async (shop: any) => {
    setPairingModalShop(shop);
    setLoadingPairing(true);
    setPairingData(null);
    try {
      const data = await api.getShopPairing(shop.id);
      setPairingData(data);
    } catch (err: any) {
      alert(err.message || 'Failed to fetch agent pairing command');
    } finally {
      setLoadingPairing(false);
    }
  };

  const handleCreateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.createShop({
        name: newShop.name,
        phone: newShop.phone,
        subscriptionMonths: newShop.durationUnit === 'months' ? newShop.durationValue : undefined,
        subscriptionDays: newShop.durationUnit === 'days' ? newShop.durationValue : undefined,
        shopkeeperUsername: newShop.shopkeeperUsername,
        shopkeeperPassword: newShop.shopkeeperPassword,
        shopkeeperEmail: newShop.shopkeeperEmail || undefined,
        allowedIps: newShop.allowedIps ? newShop.allowedIps.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [],
        ipLockEnabled: newShop.ipLockEnabled,
      });
      setCreationResult(res);
      await loadShops();
      await loadDevices();
    } catch (err: any) {
      alert(err.message || 'Failed to create shop');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveWhatsApp = async (shopId: string) => {
    setSubmitting(true);
    setWaSaveResult(null);
    setWaTestResult(null);
    try {
      const res = await api.saveWhatsAppCredentials(shopId, waCreds);
      setWaSaveResult(res);
      await loadShops();
      const updated = await api.getShop(shopId);
      setSelectedShop(updated);
      await loadWhatsAppCreds(shopId);
    } catch (err: any) {
      setWaSaveResult({ saved: false, error: err.message || 'Failed to save WhatsApp credentials' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestWhatsApp = async (shopId: string, customCreds?: any) => {
    setTestingWa(true);
    setWaTestResult(null);
    try {
      const res = await api.testWhatsAppCredentials(shopId, customCreds);
      setWaTestResult(res);
      await loadShops();
      const updated = await api.getShop(shopId);
      setSelectedShop(updated);
    } catch (err: any) {
      setWaTestResult({ ok: false, error: err.message || 'Connection test failed' });
    } finally {
      setTestingWa(false);
    }
  };

  const [updatingSub, setUpdatingSub] = useState<boolean>(false);
  const [customExpiryDate, setCustomExpiryDate] = useState<string>('');
  const [subHistory, setSubHistory] = useState<any[]>([]);
  const [loadingSubHistory, setLoadingSubHistory] = useState<boolean>(false);

  const loadSubHistory = async (shopId: string) => {
    setLoadingSubHistory(true);
    try {
      const data = await api.getSubscriptionHistory(shopId);
      setSubHistory(data || []);
    } catch {
      setSubHistory([]);
    } finally {
      setLoadingSubHistory(false);
    }
  };

  const toDatetimeLocal = (d: Date | string | null | undefined) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };

  useEffect(() => {
    if (selectedShop?.valid_until) {
      setCustomExpiryDate(toDatetimeLocal(selectedShop.valid_until));
    }
    if (selectedShop?.id && activeTab === 'subscription') {
      loadSubHistory(selectedShop.id);
    }
  }, [selectedShop?.id, selectedShop?.valid_until, activeTab]);

  const handleExtend = async (shopId: string, options: { months?: number; days?: number } | number) => {
    setUpdatingSub(true);
    try {
      await api.extendSubscription(shopId, options);
      await loadShops();
      if (selectedShop?.id === shopId) {
        setSelectedShop(await api.getShop(shopId));
      }
      await loadSubHistory(shopId);
    } catch (err: any) {
      alert(err.message || 'Failed to update subscription');
    } finally {
      setUpdatingSub(false);
    }
  };

  const handleSetExactDate = async (shopId: string) => {
    if (!customExpiryDate) return;
    setUpdatingSub(true);
    try {
      const iso = new Date(customExpiryDate).toISOString();
      await api.setManualSubscription(shopId, iso, selectedShop?.grace_days ?? 3);
      await loadShops();
      if (selectedShop?.id === shopId) {
        setSelectedShop(await api.getShop(shopId));
      }
      await loadSubHistory(shopId);
    } catch (err: any) {
      alert(err.message || 'Failed to set exact subscription date');
    } finally {
      setUpdatingSub(false);
    }
  };

  const filteredShops = shops.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.phone.includes(search) ||
      s.id.includes(search)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Shop Directory & Operations</h1>
          <p className="text-sm text-slate-400">
            Provision new print shops, extend subscriptions, configure WhatsApp vaults, and monitor agent connections.
          </p>
        </div>
        <button
          onClick={() => {
            setCreationResult(null);
            setShowAddModal(true);
          }}
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-emerald-500 hover:from-brand-500 hover:to-emerald-400 text-white font-semibold text-xs shadow-lg shadow-brand-500/20 flex items-center space-x-2 transition-all self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Provision New Shop</span>
        </button>
      </div>

      {/* Admin Section Tabs */}
      <div className="flex space-x-2 p-1.5 rounded-2xl bg-white/5 border border-white/10 text-xs max-w-lg">
        <button
          onClick={() => setAdminViewTab('shops')}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
            adminViewTab === 'shops'
              ? 'bg-emerald-600 text-white shadow'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span>1. Shops Directory</span>
          <span className="px-1.5 py-0.2 rounded-full bg-white/20 text-[10px] font-mono">
            {shops.length}
          </span>
        </button>

        <button
          onClick={() => {
            setAdminViewTab('devices');
            loadDevices();
          }}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
            adminViewTab === 'devices'
              ? 'bg-emerald-600 text-white shadow'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Laptop className="w-3.5 h-3.5" />
          <span>2. Browser Approvals</span>
          {devices.filter((d) => !d.approved).length > 0 && (
            <span className="px-1.5 py-0.2 rounded-full bg-amber-400 text-black font-bold text-[10px] animate-pulse font-mono">
              {devices.filter((d) => !d.approved).length}
            </span>
          )}
        </button>

        <button
          onClick={() => setAdminViewTab('pairing')}
          className={`flex-1 py-2 px-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
            adminViewTab === 'pairing'
              ? 'bg-emerald-600 text-white shadow'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Terminal className="w-3.5 h-3.5" />
          <span>3. Agent Deployment</span>
        </button>
      </div>

      {/* VIEW 1: SHOPS DIRECTORY */}
      {adminViewTab === 'shops' && (
        <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by shop name, phone, or ID..."
          className="w-full pl-10 pr-4 py-2.5 bg-slate-900/60 border border-white/10 rounded-xl text-white placeholder-slate-500 text-xs focus:outline-none focus:border-brand-500"
        />
      </div>

      {/* Shops Table */}
      <div className="glass-panel rounded-3xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="border-b border-white/10 text-slate-500 uppercase font-semibold bg-white/5">
              <tr>
                <th className="py-3 px-4">Shop Name</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Subscription</th>
                <th className="py-3 px-4">WhatsApp Status</th>
                <th className="py-3 px-4">Agent Status</th>
                <th className="py-3 px-4">Active Orders</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredShops.map((shop) => {
                const diff = new Date(shop.valid_until).getTime() - Date.now();
                const daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
                const isExpiringSoon = daysRemaining <= 7;

                return (
                  <tr key={shop.id} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-bold text-white text-sm">{shop.name}</div>
                      <div className="text-slate-500 text-[11px] font-mono">+{shop.phone}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                          shop.status === 'ACTIVE'
                            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                            : shop.status === 'GRACE'
                            ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400 animate-pulse'
                            : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                        }`}
                      >
                        {shop.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1.5">
                        <Calendar className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-white">{new Date(shop.valid_until).toLocaleDateString()}</span>
                      </div>
                      <span
                        className={`text-[10px] font-semibold ${
                          daysRemaining <= 0
                            ? 'text-rose-400'
                            : isExpiringSoon
                            ? 'text-amber-400'
                            : 'text-slate-400'
                        }`}
                      >
                        {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days remaining`}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {shop.baileys_status === 'CONNECTED' ? (
                        <div className="flex items-center space-x-1.5" title={`Linked WhatsApp Device: ${shop.baileys_phone || shop.phone} (100% Free Forever)`}>
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400 font-semibold text-xs">Linked (Free)</span>
                        </div>
                      ) : shop.baileys_status === 'SCAN_QR' ? (
                        <div className="flex items-center space-x-1.5" title="Awaiting QR Scan on Phone">
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                          <span className="text-amber-400 font-semibold text-xs">Scan QR</span>
                        </div>
                      ) : (shop.whatsapp_mode === 'baileys' || !shop.whatsapp_token_last4) ? (
                        <div className="flex items-center space-x-1.5" title="WhatsApp QR Linking Available (100% Free)">
                          <span className="w-2 h-2 rounded-full bg-slate-600" />
                          <span className="text-slate-400 font-medium text-xs">Not Linked</span>
                        </div>
                      ) : shop.whatsapp_verified_at && !shop.whatsapp_last_error ? (
                        <div className="flex items-center space-x-1.5" title={`Meta API Verified: ${shop.whatsapp_verified_name || shop.name}`}>
                          <span className="w-2 h-2 rounded-full bg-emerald-400" />
                          <span className="text-emerald-400 font-semibold text-xs">Connected (Meta)</span>
                        </div>
                      ) : shop.whatsapp_last_error ? (
                        <div className="flex items-center space-x-1.5" title={shop.whatsapp_last_error}>
                          <span className="w-2 h-2 rounded-full bg-rose-500" />
                          <span className="text-rose-400 font-semibold text-xs">
                            {shop.whatsapp_last_error.toLowerCase().includes('expired') ? 'Meta Token Expired' : 'Meta Error'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1.5" title="No WhatsApp linked yet">
                          <span className="w-2 h-2 rounded-full bg-slate-600" />
                          <span className="text-slate-400 font-medium text-xs">Not Linked</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-1.5">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            shop.agent_online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
                          }`}
                        />
                        <span className={shop.agent_online ? 'text-emerald-400' : 'text-slate-500'}>
                          {shop.agent_online ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-white">
                      {shop.active_orders_count || 0}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDeviceShopFilter(shop.id);
                          setAdminViewTab('devices');
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 font-medium text-xs inline-flex items-center space-x-1 transition-colors cursor-pointer"
                        title="View browser authorizations for this shop"
                      >
                        <Laptop className="w-3 h-3 text-cyan-400" />
                        <span>Browsers</span>
                        {devices.filter((d) => (d.shop_id === shop.id || d.shop_slug === shop.id) && !d.approved).length > 0 && (
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse ml-0.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenPairing(shop)}
                        className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-medium text-xs inline-flex items-center space-x-1 transition-colors"
                        title="Display Agent pairing command for this shop PC"
                      >
                        <Laptop className="w-3 h-3" />
                        <span>Pair Agent</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedShop(shop);
                          setActiveTab('subscription');
                        }}
                        className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium text-xs transition-colors"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => onOpenShopDashboard(shop.id)}
                        className="px-3 py-1.5 rounded-lg bg-brand-600/20 hover:bg-brand-600/30 border border-brand-500/30 text-brand-300 font-medium text-xs inline-flex items-center space-x-1 transition-colors"
                      >
                        <span>Open View</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      )}

      {/* VIEW 2: BROWSER DEVICE APPROVALS */}
      {adminViewTab === 'devices' && (() => {
        const filteredDevices = devices.filter((d) => {
          if (selectedDeviceShopFilter !== 'all' && d.shop_id !== selectedDeviceShopFilter && d.shop_slug !== selectedDeviceShopFilter) {
            return false;
          }
          if (deviceSearchQuery.trim()) {
            const q = deviceSearchQuery.toLowerCase();
            return (
              (d.shop_name && d.shop_name.toLowerCase().includes(q)) ||
              (d.username && d.username.toLowerCase().includes(q)) ||
              (d.label && d.label.toLowerCase().includes(q)) ||
              (d.last_ip && d.last_ip.includes(q))
            );
          }
          return true;
        });

        const targetShops = selectedDeviceShopFilter === 'all'
          ? shops
          : shops.filter((s) => s.id === selectedDeviceShopFilter);

        const shopDeviceGroups = targetShops.map((s) => {
          const sDevs = filteredDevices.filter((d) => d.shop_id === s.id || d.shop_slug === s.id);
          return { shop: s, devices: sDevs };
        }).filter((g) => selectedDeviceShopFilter !== 'all' || g.devices.length > 0 || !deviceSearchQuery.trim());

        return (
          <div className="space-y-6">
            <div className="glass-panel p-5 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-bold text-white flex items-center space-x-2">
                  <Laptop className="w-5 h-5 text-emerald-400" />
                  <span>Shopkeeper Browser Approvals</span>
                </h2>
                <p className="text-xs text-slate-400 mt-1 max-w-2xl">
                  Browser Lock Security: Counter staff can only access the counter terminal from web browsers you authorize. Each shop is managed independently — approve or revoke access for each shopkeeper browser below.
                </p>
              </div>
              <button
                onClick={loadDevices}
                className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold flex items-center space-x-1.5 transition cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingDevices ? 'animate-spin' : ''}`} />
                <span>Refresh Device Requests</span>
              </button>
            </div>

            {/* Shop Filter Selector & Search */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-400 mr-1">Filter Shop:</span>
                <button
                  type="button"
                  onClick={() => setSelectedDeviceShopFilter('all')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition cursor-pointer flex items-center space-x-1.5 ${
                    selectedDeviceShopFilter === 'all'
                      ? 'bg-emerald-600 text-white shadow'
                      : 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white border border-white/10'
                  }`}
                >
                  <span>All Shops</span>
                  <span className="px-1.5 py-0.2 rounded-full bg-white/20 text-[10px] font-mono">
                    {devices.length}
                  </span>
                </button>

                {shops.map((s) => {
                  const sDevs = devices.filter((d) => d.shop_id === s.id || d.shop_slug === s.id);
                  const sPending = sDevs.filter((d) => !d.approved).length;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedDeviceShopFilter(s.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition cursor-pointer flex items-center space-x-1.5 ${
                        selectedDeviceShopFilter === s.id
                          ? 'bg-emerald-600 text-white shadow'
                          : 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white border border-white/10'
                      }`}
                    >
                      <span>{s.name}</span>
                      <span className="px-1.5 py-0.2 rounded-full bg-white/20 text-[10px] font-mono">
                        {sDevs.length}
                      </span>
                      {sPending > 0 && (
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse ml-0.5" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="relative w-full sm:w-64">
                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={deviceSearchQuery}
                  onChange={(e) => setDeviceSearchQuery(e.target.value)}
                  placeholder="Search username, IP, browser..."
                  className="w-full pl-9 pr-3 py-1.5 bg-slate-900/60 border border-white/10 rounded-xl text-white placeholder-slate-500 text-xs focus:outline-none focus:border-brand-500"
                />
              </div>
            </div>

            {/* Dedicated Shop-by-Shop Browser Containers */}
            {shopDeviceGroups.length === 0 ? (
              <div className="glass-panel p-12 text-center text-slate-400 rounded-3xl border border-white/10">
                {loadingDevices ? 'Loading device authorizations...' : 'No shopkeeper browser devices found.'}
              </div>
            ) : (
              shopDeviceGroups.map(({ shop: s, devices: shopDevs }) => (
                <div key={s.id} className="glass-panel rounded-3xl border border-white/10 overflow-hidden space-y-0">
                  {/* Shop Section Header */}
                  <div className="px-5 py-3.5 bg-white/5 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-xs">
                        {s.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-white text-sm">{s.name}</span>
                          <span className="text-[10px] font-mono text-slate-400">{s.phone}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center space-x-2 mt-0.5">
                          <span>Authorized Shopkeeper Account:</span>
                          <span className="font-mono text-emerald-400 font-semibold px-1.5 py-0.2 rounded bg-emerald-500/10 border border-emerald-500/20">
                            {shopDevs[0]?.username || s.shopkeeper_username || 'Counter Staff'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-slate-400 font-medium">
                        {shopDevs.filter((d) => d.approved).length} Approved
                      </span>
                      {shopDevs.filter((d) => !d.approved).length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold animate-pulse">
                          {shopDevs.filter((d) => !d.approved).length} Pending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Table for this shop */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-300">
                      <thead className="border-b border-white/10 text-slate-500 uppercase font-semibold bg-white/[0.02]">
                        <tr>
                          <th className="py-3 px-4">Counter Username</th>
                          <th className="py-3 px-4">Browser Device Label</th>
                          <th className="py-3 px-4">Last IP</th>
                          <th className="py-3 px-4">Accepted / Requested Date</th>
                          <th className="py-3 px-4">Last Activity</th>
                          <th className="py-3 px-4">Approval Status</th>
                          <th className="py-3 px-4 text-right">Access Control</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {shopDevs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="py-8 text-center text-slate-500">
                              No browsers registered for {s.name} yet. Counter staff will appear here when logging in from /shop.
                            </td>
                          </tr>
                        ) : (
                          shopDevs.map((dev) => (
                            <tr key={dev.id} className="hover:bg-white/5 transition-colors">
                              <td className="py-3 px-4 font-mono text-emerald-400 font-medium">
                                <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">
                                  {dev.username || dev.email || 'Counter Staff'}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="text-white font-medium">{dev.label || 'Web Browser'}</div>
                                <div className="text-[10px] text-slate-500 font-mono">ID: {dev.id.slice(0, 8)}...</div>
                              </td>
                              <td className="py-3 px-4 font-mono text-slate-300">{dev.last_ip || 'Local Network'}</td>
                              <td className="py-3 px-4">
                                {dev.approved && dev.approved_at ? (
                                  <div>
                                    <div className="text-emerald-400 font-medium">
                                      {new Date(dev.approved_at).toLocaleString()}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Approved</div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="text-amber-400 font-medium">
                                      {dev.created_at ? new Date(dev.created_at).toLocaleString() : 'Recent'}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Requested</div>
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4 text-slate-400">
                                {dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleString() : 'Never'}
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                                    dev.approved
                                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                      : 'bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse'
                                  }`}
                                >
                                  {dev.approved ? 'APPROVED BROWSER' : 'PENDING APPROVAL'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  {dev.approved ? (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleDeviceApproval(dev.id, false)}
                                      className="px-3 py-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 font-semibold text-xs transition cursor-pointer"
                                    >
                                      Revoke Access
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleDeviceApproval(dev.id, true)}
                                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/30 transition cursor-pointer"
                                    >
                                      Approve Browser
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteDevice(dev.id)}
                                    title="Delete Browser Approval"
                                    className="px-2.5 py-1.5 rounded-lg bg-red-950/30 hover:bg-red-900/50 text-red-400 hover:text-red-200 border border-red-800/40 font-semibold text-xs transition cursor-pointer flex items-center space-x-1"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })()}

      {/* VIEW 3: AGENT PAIRING & DEPLOYMENT */}
      {adminViewTab === 'pairing' && (
        <div className="space-y-6">
          <div className="glass-panel p-6 rounded-3xl border border-white/10 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
                <Terminal className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">First-Time Shop PC Print Daemon Pairing</h2>
                <p className="text-xs text-slate-400 mt-1">
                  Once paired on the shopkeeper PC, the daemon permanently writes configuration to <code>%USERPROFILE%/.printbridge/config.json</code>. It connects outbound over WebSocket and remains permanently bound to that shop even if the shop changes dynamic IP, router, or WiFi network!
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
              {shops.map((shop) => (
                <div key={shop.id} className="glass-panel p-4 rounded-2xl border border-slate-800 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-white text-sm">{shop.name}</h4>
                      <div className="text-[11px] text-slate-400 font-mono">+{shop.phone}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      shop.agent_online ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-800 text-slate-400'
                    }`}>
                      {shop.agent_online ? 'Agent Online' : 'Agent Offline'}
                    </span>
                  </div>

                  <div className="bg-slate-900/80 p-2.5 rounded-xl border border-slate-800 font-mono text-[11px] text-slate-300 break-all select-all">
                    node dist/index.js --pair {shop.id}
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(`node dist/index.js --pair ${shop.id}`, `pair_${shop.id}`)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600/30 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-500/40 text-xs font-semibold flex items-center space-x-1"
                    >
                      {copiedKey === `pair_${shop.id}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedKey === `pair_${shop.id}` ? 'Copied' : 'Copy Command'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenPairing(shop)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
                    >
                      Details & Token
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: SHOP AGENT PAIRING POPUP */}
      {pairingModalShop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md">
          <div className="glass-panel max-w-lg w-full p-6 rounded-3xl border border-emerald-500/40 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center space-x-2.5">
                <Laptop className="w-5 h-5 text-emerald-400" />
                <h3 className="text-sm font-bold text-white">
                  Agent Pairing — {pairingModalShop.name}
                </h3>
              </div>
              <button
                onClick={() => setPairingModalShop(null)}
                className="text-slate-400 hover:text-white p-1"
              >
                ✕
              </button>
            </div>

            {loadingPairing ? (
              <div className="py-8 text-center text-xs text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto text-emerald-400 mb-2" />
                Loading pairing details...
              </div>
            ) : (
              <div className="space-y-3.5 text-xs">
                <p className="text-slate-300">
                  Run this command once in the PowerShell / Command Prompt terminal on the shopkeeper PC:
                </p>

                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 space-y-2">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Pairing Command:</div>
                  <div className="font-mono text-emerald-400 select-all break-all text-xs">
                    {pairingData?.command || `node dist/index.js --pair ${pairingModalShop.id}`}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(pairingData?.command || `node dist/index.js --pair ${pairingModalShop.id}`, 'modal_cmd')}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 font-bold text-xs inline-flex items-center space-x-1"
                  >
                    {copiedKey === 'modal_cmd' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'modal_cmd' ? 'Copied' : 'Copy Command'}</span>
                  </button>
                </div>

                <div className="p-3 rounded-xl bg-white/5 border border-white/5 space-y-1.5 text-slate-400 text-[11px]">
                  <div className="font-bold text-white">Permanent Persistence Guarantee:</div>
                  <div>• Configuration is permanently written to <code>%USERPROFILE%/.printbridge/config.json</code></div>
                  <div>• Outbound WebSocket connection bypasses firewall, NAT, and port-forwarding</div>
                  <div>• Dynamic IP changes or switching between Wi-Fi and hotspot will NEVER unpair this PC!</div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setPairingModalShop(null)}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-white font-semibold text-xs"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Shop Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-xl w-full glass-panel p-6 sm:p-8 rounded-3xl border border-white/10 max-h-[90vh] overflow-y-auto space-y-5">
            {!creationResult ? (
              <>
                <div className="flex justify-between items-center pb-3 border-b border-white/10">
                  <h3 className="text-lg font-bold text-white">Provision New Print Shop</h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-slate-500 hover:text-white"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleCreateShop} className="space-y-4 text-xs">
                  <div>
                    <label className="block text-slate-300 font-semibold mb-1">Shop Name</label>
                    <input
                      type="text"
                      required
                      value={newShop.name}
                      onChange={(e) => setNewShop({ ...newShop, name: e.target.value })}
                      placeholder="Campus Xerox & Print Station"
                      className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1">WhatsApp Phone Number</label>
                      <input
                        type="text"
                        required
                        value={newShop.phone}
                        onChange={(e) => setNewShop({ ...newShop, phone: e.target.value })}
                        placeholder="919876543210"
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1">Initial Subscription</label>
                      <select
                        value={newShop.durationUnit === 'days' ? `d_${newShop.durationValue}` : `m_${newShop.durationValue}`}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.startsWith('d_')) {
                            const days = parseInt(val.replace('d_', ''), 10);
                            setNewShop({ ...newShop, durationUnit: 'days', durationValue: days });
                          } else {
                            const months = parseInt(val.replace('m_', ''), 10);
                            setNewShop({ ...newShop, durationUnit: 'months', durationValue: months });
                          }
                        }}
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      >
                        <option value="d_1">1 Day (Quick Trial)</option>
                        <option value="d_3">3 Days (Trial)</option>
                        <option value="d_7">7 Days (1 Week)</option>
                        <option value="d_15">15 Days</option>
                        <option value="m_1">1 Month</option>
                        <option value="m_3">3 Months</option>
                        <option value="m_6">6 Months</option>
                        <option value="m_12">12 Months (1 Year)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1">
                        Shopkeeper Username
                      </label>
                      <input
                        type="text"
                        required
                        value={newShop.shopkeeperUsername}
                        onChange={(e) => setNewShop({ ...newShop, shopkeeperUsername: e.target.value })}
                        placeholder="e.g. fastprint_counter1"
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1">
                        Shopkeeper Email <span className="text-emerald-400 font-normal">(Optional for OTPs)</span>
                      </label>
                      <input
                        type="email"
                        value={newShop.shopkeeperEmail}
                        onChange={(e) => setNewShop({ ...newShop, shopkeeperEmail: e.target.value })}
                        placeholder="e.g. shop@example.com"
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 font-semibold mb-1">Temporary Password</label>
                      <input
                        type="password"
                        required
                        minLength={8}
                        value={newShop.shopkeeperPassword}
                        onChange={(e) => setNewShop({ ...newShop, shopkeeperPassword: e.target.value })}
                        placeholder="Min 8 characters"
                        className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-slate-300 font-semibold">
                        Allowed Shop IPs (Optional, comma-separated)
                      </label>
                      <div className="flex items-center space-x-1.5">
                        <input
                          type="checkbox"
                          id="new_ip_lock"
                          checked={newShop.ipLockEnabled}
                          onChange={(e) => setNewShop({ ...newShop, ipLockEnabled: e.target.checked })}
                          className="w-3.5 h-3.5 rounded text-brand-600 bg-slate-900 border-white/20 cursor-pointer"
                        />
                        <label htmlFor="new_ip_lock" className="text-slate-400 text-[11px] cursor-pointer">
                          Enforce IP Lock
                        </label>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={newShop.allowedIps}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewShop({
                          ...newShop,
                          allowedIps: val,
                          ipLockEnabled: newShop.ipLockEnabled || Boolean(val.trim()),
                        });
                      }}
                      placeholder="e.g. 192.168.1.100, 10.0.0.0/24"
                      className="w-full px-3.5 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Strict network lock: Only logins from these specific IP addresses will be allowed for this shop.
                    </p>
                  </div>

                  <div className="pt-3 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 rounded-xl bg-white/5 text-slate-300 hover:bg-white/10"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold shadow-md shadow-brand-500/20"
                    >
                      {submitting ? 'Creating...' : 'Create Shop'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Success / Credentials Display (Shown Once) */
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                  <h4 className="font-bold text-sm">Shop Successfully Provisioned!</h4>
                  <p className="text-xs text-emerald-200 mt-1">
                    Store the Agent Pairing Command and Login Credentials below.
                  </p>
                </div>

                <div className="space-y-3 text-xs">
                  <div>
                    <label className="text-slate-400 font-semibold">1-Click Shop PC Agent Pairing Command:</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="text"
                        readOnly
                        value={`node dist/index.js --pair ${creationResult.shop?.id || ''}:${creationResult.agentKey}`}
                        className="w-full p-2.5 bg-slate-900 rounded-xl font-mono text-xs text-emerald-400 border border-white/10"
                      />
                      <button
                        type="button"
                        onClick={() => handleCopy(`node dist/index.js --pair ${creationResult.shop?.id || ''}:${creationResult.agentKey}`, 'paircmd')}
                        className="px-3 py-2 bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 rounded-xl font-bold text-xs"
                      >
                        {copiedKey === 'paircmd' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-slate-400 font-semibold">Agent Key:</label>
                    <div className="p-2.5 bg-slate-900 rounded-xl font-mono text-emerald-400 break-all select-all border border-white/10 text-xs">
                      {creationResult.agentKey}
                    </div>
                  </div>

                  <div>
                    <label className="text-slate-400 font-semibold">Meta Cloud API Webhook URL:</label>
                    <div className="p-2.5 bg-slate-900 rounded-xl font-mono text-slate-200 break-all select-all border border-white/10 text-xs">
                      {creationResult.metaWebhookUrl}
                    </div>
                  </div>

                  <div>
                    <label className="text-slate-400 font-semibold">Meta Verify Token:</label>
                    <div className="p-2.5 bg-slate-900 rounded-xl font-mono text-slate-200 select-all border border-white/10 text-xs">
                      {creationResult.metaVerifyToken}
                    </div>
                  </div>
                </div>

                <div className="pt-3 flex justify-end">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setCreationResult(null);
                    }}
                    className="px-5 py-2 rounded-xl bg-brand-600 text-white font-semibold"
                  >
                    Done & Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shop Detail Drawer / Modal */}
      {selectedShop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="max-w-2xl w-full glass-panel p-6 sm:p-8 rounded-3xl border border-white/10 max-h-[90vh] overflow-y-auto space-y-6">
            <div className="flex justify-between items-center pb-3 border-b border-white/10">
              <div>
                <h3 className="text-lg font-bold text-white">{selectedShop.name}</h3>
                <span className="text-xs text-slate-500 font-mono">ID: {selectedShop.id}</span>
              </div>
              <button onClick={() => setSelectedShop(null)} className="text-slate-500 hover:text-white">
                ✕
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap gap-2 p-1.5 rounded-2xl bg-white/5 border border-white/10 text-xs">
              {(['subscription', 'whatsapp', 'switches', 'access', 'agent', 'danger'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    if (tab === 'access' && selectedShop) {
                      loadShopkeeperCreds(selectedShop.id);
                      loadShopModalDevices(selectedShop.id);
                      setShopIpLock({
                        ipLockEnabled: Boolean(selectedShop.ip_lock_enabled),
                        allowedIps: (selectedShop.allowed_ips || []).join(', '),
                      });
                    }
                  }}
                  className={`px-3 py-1.5 rounded-xl font-semibold capitalize transition-all ${
                    activeTab === tab ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab: Subscription */}
            {activeTab === 'subscription' && (
              <div className="space-y-4 text-xs">
                {/* 1. Current Valid Until & Remaining Days */}
                {(() => {
                  const validDate = new Date(selectedShop.valid_until);
                  const diffMs = validDate.getTime() - Date.now();
                  const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                  return (
                    <div className="p-4 rounded-2xl bg-slate-900/80 border border-white/10 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-slate-400 font-semibold flex items-center gap-2">
                          <span>Valid Until:</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                              daysRemaining > 5
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : daysRemaining > 0
                                ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                                : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                            }`}
                          >
                            {daysRemaining > 0
                              ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`
                              : daysRemaining === 0
                              ? 'Expires today'
                              : `Expired ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} ago`}
                          </span>
                        </div>
                        <div className="text-base font-bold text-white mt-1 font-mono">
                          {validDate.toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true,
                          })}
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase border ${
                          selectedShop.status === 'ACTIVE'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                            : selectedShop.status === 'GRACE'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                            : 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                        }`}
                      >
                        {selectedShop.status}
                      </span>
                    </div>
                  );
                })()}

                {/* 2. Quick Increase & Decrease Controls */}
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <label className="text-slate-300 font-bold block text-sm">Subscription Duration Controls:</label>
                    {updatingSub && <span className="text-brand-400 text-[11px] animate-pulse">Updating subscription...</span>}
                  </div>

                  {/* Adjust by Days */}
                  <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/08 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-300 font-semibold">Adjust by Days:</span>
                      <span className="text-[10px] text-slate-400">Green = Increase (+), Red = Decrease (-)</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase text-emerald-400 w-12">Increase:</span>
                      {[
                        { days: 1, label: '+1 Day' },
                        { days: 3, label: '+3 Days' },
                        { days: 7, label: '+7 Days (1 Wk)' },
                        { days: 15, label: '+15 Days' },
                        { days: 30, label: '+30 Days' },
                      ].map((item) => (
                        <button
                          key={'plus-d-' + item.days}
                          disabled={updatingSub}
                          onClick={() => handleExtend(selectedShop.id, { days: item.days })}
                          className="px-2.5 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-600 border border-emerald-500/30 text-emerald-300 hover:text-white font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/05">
                      <span className="text-[10px] font-bold uppercase text-rose-400 w-12">Decrease:</span>
                      {[
                        { days: -1, label: '-1 Day' },
                        { days: -3, label: '-3 Days' },
                        { days: -7, label: '-7 Days (1 Wk)' },
                        { days: -15, label: '-15 Days' },
                        { days: -30, label: '-30 Days' },
                      ].map((item) => (
                        <button
                          key={'minus-d-' + item.days}
                          disabled={updatingSub}
                          onClick={() => handleExtend(selectedShop.id, { days: item.days })}
                          className="px-2.5 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-600 border border-rose-500/30 text-rose-300 hover:text-white font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Adjust by Months */}
                  <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/08 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-300 font-semibold">Adjust by Months:</span>
                      <span className="text-[10px] text-slate-400">Green = Increase (+), Red = Decrease (-)</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase text-emerald-400 w-12">Increase:</span>
                      {[
                        { months: 1, label: '+1 Month' },
                        { months: 3, label: '+3 Months' },
                        { months: 6, label: '+6 Months' },
                        { months: 12, label: '+12 Months (1 Yr)' },
                      ].map((item) => (
                        <button
                          key={'plus-m-' + item.months}
                          disabled={updatingSub}
                          onClick={() => handleExtend(selectedShop.id, { months: item.months })}
                          className="px-2.5 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-600 border border-emerald-500/30 text-emerald-300 hover:text-white font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/05">
                      <span className="text-[10px] font-bold uppercase text-rose-400 w-12">Decrease:</span>
                      {[
                        { months: -1, label: '-1 Month' },
                        { months: -3, label: '-3 Months' },
                        { months: -6, label: '-6 Months' },
                        { months: -12, label: '-12 Months (1 Yr)' },
                      ].map((item) => (
                        <button
                          key={'minus-m-' + item.months}
                          disabled={updatingSub}
                          onClick={() => handleExtend(selectedShop.id, { months: item.months })}
                          className="px-2.5 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-600 border border-rose-500/30 text-rose-300 hover:text-white font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Set Exact Expiry Date & Time */}
                  <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/08 space-y-2">
                    <span className="text-[11px] text-slate-300 font-semibold block">Set Exact Expiry Date & Time:</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        value={customExpiryDate}
                        onChange={(e) => setCustomExpiryDate(e.target.value)}
                        className="px-3 py-1.5 rounded-xl bg-slate-950 border border-white/10 text-white font-mono text-xs focus:outline-none focus:border-brand-500 flex-1 min-w-[200px]"
                      />
                      <button
                        type="button"
                        disabled={updatingSub || !customExpiryDate}
                        onClick={() => handleSetExactDate(selectedShop.id)}
                        className="px-3.5 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold transition-colors cursor-pointer"
                      >
                        {updatingSub ? 'Saving...' : 'Set Date & Time'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* 3. Subscription Action & Operations */}
                <div className="pt-2 flex items-center justify-between border-t border-white/10">
                  <button
                    onClick={async () => {
                      await api.toggleSuspendShop(selectedShop.id);
                      await loadShops();
                      setSelectedShop(await api.getShop(selectedShop.id));
                      await loadSubHistory(selectedShop.id);
                    }}
                    className={`px-4 py-2 rounded-xl font-semibold border ${
                      selectedShop.status === 'SUSPENDED'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                        : 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
                    } transition-colors cursor-pointer`}
                  >
                    {selectedShop.status === 'SUSPENDED' ? 'Resume Shop Operations' : 'Suspend Shop Operations'}
                  </button>
                </div>

                {/* 4. Subscription History & Admin Audit Log */}
                <div className="pt-3 border-t border-white/10 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-brand-400" />
                      <span className="font-bold text-slate-200 text-xs">Subscription Change History & Admin Audit Log:</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => loadSubHistory(selectedShop.id)}
                      className="text-slate-400 hover:text-white text-[11px] flex items-center gap-1 transition-colors cursor-pointer"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingSubHistory ? 'animate-spin' : ''}`} />
                      <span>Refresh Log</span>
                    </button>
                  </div>

                  {loadingSubHistory && subHistory.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-xs">Loading subscription history...</div>
                  ) : subHistory.length === 0 ? (
                    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/05 text-center text-slate-500 text-xs">
                      No subscription changes recorded yet for this shop.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {subHistory.map((h) => {
                        const isIncrease = h.action === 'INCREASE' || h.action === 'INITIAL' || (h.delta && h.delta.startsWith('+'));
                        const isDecrease = h.action === 'DECREASE' || (h.delta && h.delta.startsWith('-'));
                        return (
                          <div
                            key={h.id}
                            className="p-2.5 rounded-xl bg-white/[0.03] border border-white/06 hover:border-white/10 flex flex-wrap items-center justify-between gap-2 text-xs transition-colors"
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                                  isIncrease
                                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                    : isDecrease
                                    ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                                    : 'bg-brand-500/15 text-brand-300 border border-brand-500/30'
                                }`}
                              >
                                {h.delta || h.action}
                              </span>
                              <div>
                                <div className="text-white font-medium text-[11px] flex items-center gap-1.5">
                                  <span className="text-slate-400">Admin:</span>
                                  <span className="font-semibold text-slate-200">{h.adminEmail}</span>
                                  <span className="text-[10px] text-slate-500 uppercase font-mono">({h.adminRole})</span>
                                </div>
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  {h.newValidUntil ? `New Expiry: ${new Date(h.newValidUntil).toLocaleString()}` : h.summary}
                                </div>
                              </div>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono text-right">
                              {new Date(h.createdAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tab: WhatsApp Encrypted Vault */}
            {activeTab === 'whatsapp' && (
              <div className="space-y-5 text-xs">
                {/* 1. PRIMARY OPTION: WhatsApp Web Multi-Device (100% Free Forever) */}
                <div className={`p-5 rounded-2xl border transition-all ${
                  baileysState?.status === 'CONNECTED'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-white/5 border-white/10'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-white/10 gap-3">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-xl ${baileysState?.status === 'CONNECTED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-slate-300'}`}>
                        <Smartphone className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-bold text-white text-base">WhatsApp Web Linking</h4>
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            100% Free Forever (₹0)
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          Scan QR from normal WhatsApp or WhatsApp Business mobile app. No Meta fees, no card details, no expiring tokens!
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {baileysState?.status === 'CONNECTED' ? (
                        <button
                          onClick={() => handleDisconnectBaileys(selectedShop.id)}
                          disabled={loadingBaileys}
                          className="px-3.5 py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 font-semibold flex items-center space-x-1.5 transition-colors disabled:opacity-50 cursor-pointer text-xs"
                        >
                          <span>Unlink WhatsApp</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStartBaileys(selectedShop.id)}
                          disabled={loadingBaileys}
                          className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold flex items-center space-x-2 transition-all disabled:opacity-50 cursor-pointer shadow-lg shadow-emerald-500/20 text-xs"
                        >
                          <QrCode className="w-4 h-4" />
                          <span>{loadingBaileys ? 'Connecting...' : (baileysState?.status === 'SCAN_QR' ? 'Refresh QR Code' : 'Scan QR to Link WhatsApp')}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {baileysState?.status === 'CONNECTED' ? (
                    <div className="pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-white font-bold text-sm">
                            Connected Number: {baileysState.phone || (selectedShop.phone ? `+${selectedShop.phone}` : 'Active Phone')}
                          </span>
                        </div>
                        <div className="text-slate-400 text-[11px]">
                          {baileysState.connectedAt ? `Linked on ${new Date(baileysState.connectedAt).toLocaleString()}` : 'Live Multi-Device Connection'} • Incoming PDFs & photos from customers automatically appear in live queue.
                        </div>
                      </div>
                      <div className="px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-semibold text-xs flex items-center space-x-1.5">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Ready & Active</span>
                      </div>
                    </div>
                  ) : baileysState?.status === 'SCAN_QR' && baileysState.qrDataUrl ? (
                    <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                      <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-xl">
                        <img src={baileysState.qrDataUrl} alt="WhatsApp QR Code" className="w-52 h-52 object-contain" />
                        <span className="text-slate-800 text-[12px] font-bold mt-2 animate-pulse flex items-center gap-1.5">
                          <QrCode className="w-4 h-4 text-emerald-600" /> Point WhatsApp Camera Here
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="font-bold text-white text-sm">How to link on your mobile phone:</div>
                        <ol className="space-y-2 text-slate-300 text-[12px] list-decimal list-inside">
                          <li>Open <b>WhatsApp</b> on the shop's phone.</li>
                          <li>Tap <b>Menu (⋮)</b> on Android or <b>Settings</b> on iPhone.</li>
                          <li>Select <b>Linked Devices</b>.</li>
                          <li>Tap <b>Link a Device</b> and point your camera at the QR code.</li>
                        </ol>
                        <div className="pt-2 flex items-center space-x-3">
                          <button
                            onClick={() => handleStartBaileys(selectedShop.id, true)}
                            disabled={loadingBaileys}
                            className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs flex items-center space-x-1.5 transition-colors cursor-pointer"
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingBaileys ? 'animate-spin' : ''}`} />
                            <span>Regenerate QR</span>
                          </button>
                          <span className="text-emerald-400 text-[11px] font-medium flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                            Listening for phone scan...
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="pt-3 text-slate-400 text-xs flex items-center justify-between">
                      <div>
                        Status: <span className="text-slate-300 font-semibold">Not Linked</span>. Click <b>"Scan QR to Link WhatsApp"</b> to connect the shop's phone in 5 seconds.
                      </div>
                    </div>
                  )}
                </div>

                {/* Collapsible toggle for legacy Meta option */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowMetaAccordion(!showMetaAccordion)}
                    className="flex items-center justify-between w-full p-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 text-xs transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-slate-300">
                      {showMetaAccordion ? '▼ Hide Meta Developer Cloud API Option' : '▶ Advanced: Meta WhatsApp Cloud API (Option 1)'}
                    </span>
                    <span className="text-[11px] text-slate-500">Developer tokens & charges</span>
                  </button>
                </div>

                {showMetaAccordion && (
                  <div className="space-y-4 pt-2">
                {/* 1. WhatsApp Connection Status Card */}
                <div className={`p-4 rounded-2xl border transition-all ${
                  selectedShop.whatsapp_verified_at && !selectedShop.whatsapp_last_error
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : selectedShop.whatsapp_last_error
                    ? 'bg-rose-500/10 border-rose-500/30'
                    : 'bg-white/5 border-white/10'
                }`}>
                  <div className="flex items-center justify-between pb-3 border-b border-white/10">
                    <div className="flex items-center space-x-2">
                      <span className={`w-3 h-3 rounded-full ${
                        selectedShop.whatsapp_verified_at && !selectedShop.whatsapp_last_error
                          ? 'bg-emerald-400 shadow shadow-emerald-400 animate-pulse'
                          : selectedShop.whatsapp_last_error
                          ? 'bg-rose-500'
                          : selectedShop.whatsapp_token_last4
                          ? 'bg-amber-400'
                          : 'bg-slate-500'
                      }`} />
                      <span className="font-bold text-white text-sm">
                        {selectedShop.whatsapp_verified_at && !selectedShop.whatsapp_last_error
                          ? 'Meta WhatsApp Cloud API: Connected'
                          : selectedShop.whatsapp_last_error?.toLowerCase().includes('expired')
                          ? 'Meta WhatsApp Cloud API: Token Expired'
                          : selectedShop.whatsapp_last_error
                          ? 'Meta WhatsApp Cloud API: Connection Error'
                          : selectedShop.whatsapp_token_last4
                          ? 'Meta WhatsApp Cloud API: Needs Verification'
                          : 'Meta WhatsApp Cloud API: Not Configured'}
                      </span>
                    </div>

                    <button
                      id="btn-admin-test-whatsapp"
                      onClick={() => handleTestWhatsApp(selectedShop.id)}
                      disabled={testingWa || !selectedShop.whatsapp_token_last4}
                      className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center space-x-1.5 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${testingWa ? 'animate-spin' : ''}`} />
                      <span>{testingWa ? 'Testing Meta...' : 'Test Meta Connection'}</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                    <div>
                      <span className="text-slate-400 block text-[11px]">Verified Business Name:</span>
                      <span className="font-semibold text-white">
                        {selectedShop.whatsapp_verified_name || selectedShop.name}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Display Phone Number:</span>
                      <span className="font-semibold text-white">
                        {selectedShop.whatsapp_display_phone_number
                          ? (selectedShop.whatsapp_display_phone_number.startsWith('+') ? selectedShop.whatsapp_display_phone_number : '+' + selectedShop.whatsapp_display_phone_number)
                          : (selectedShop.phone ? '+' + selectedShop.phone : 'Not Set')}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Phone Number ID:</span>
                      <span className="font-mono text-slate-300">
                        {selectedShop.whatsapp_phone_number_id || 'Not configured'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Token Vault:</span>
                      <span className="font-mono text-emerald-400 font-bold">
                        {selectedShop.whatsapp_token_last4 ? `••••••••••••${selectedShop.whatsapp_token_last4}` : 'None'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Last Webhook Received:</span>
                      <span className="text-slate-300">
                        {selectedShop.whatsapp_last_webhook_at
                          ? new Date(selectedShop.whatsapp_last_webhook_at).toLocaleString()
                          : 'Awaiting first event'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Webhook Status:</span>
                      <span className="font-semibold text-slate-200">
                        {selectedShop.whatsapp_last_webhook_status || 'IDLE'}
                      </span>
                    </div>
                  </div>

                  {selectedShop.whatsapp_last_error && (
                    <div className="mt-3 p-3 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-300 flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-400" />
                      <div>
                        <div className="font-bold">Meta Error Detected:</div>
                        <div className="font-mono text-[11px] mt-0.5">{selectedShop.whatsapp_last_error}</div>
                      </div>
                    </div>
                  )}

                  {waTestResult && (
                    <div className={`mt-3 p-3 rounded-xl border flex items-start space-x-2 ${
                      waTestResult.ok ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'bg-rose-500/20 border-rose-500/40 text-rose-300'
                    }`}>
                      {waTestResult.ok ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-400" />
                      )}
                      <div>
                        <div className="font-bold">{waTestResult.ok ? 'Meta Cloud API Connection Verified!' : 'Meta API Connection Failed'}</div>
                        <div className="text-[11px] mt-0.5">
                          {waTestResult.ok
                            ? `Connected to Meta as "${waTestResult.phoneData?.verifiedName}" (${waTestResult.phoneData?.displayPhoneNumber}). Quality rating: ${waTestResult.phoneData?.qualityRating}.`
                            : waTestResult.error}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. Meta Webhook Configuration Details */}
                <div className="p-4 rounded-2xl bg-slate-900/90 border border-white/10 space-y-3">
                  <div className="flex items-center space-x-2">
                    <ShieldCheck className="w-4 h-4 text-brand-400" />
                    <h4 className="font-bold text-white text-sm">Meta Developer Webhook Setup</h4>
                  </div>
                  <p className="text-slate-400 text-xs">
                    In your Meta Developer Console (<b>developers.facebook.com</b>) &gt; WhatsApp &gt; Configuration:
                  </p>

                  <div className="space-y-2">
                    {/* Primary Webhook URL */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-400 text-[11px]">
                          Callback URL (Must be Public HTTPS for Meta):
                        </label>
                        {window.location.protocol === 'https:' ? (
                          <span className="text-[10px] text-emerald-400 font-mono">✓ Active HTTPS SSL</span>
                        ) : (
                          <span className="text-[10px] text-amber-400 font-mono">⚠️ Requires HTTPS (DuckDNS / SSL)</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          readOnly
                          value={window.location.protocol === 'https:' ? `${window.location.origin}/webhook/${selectedShop.id}` : `https://${window.location.hostname}/webhook/${selectedShop.id}`}
                          className="w-full px-3 py-2 bg-slate-950 border border-emerald-500/30 rounded-xl text-emerald-300 font-mono text-[11px]"
                        />
                        <button
                          type="button"
                          onClick={() => handleCopy(window.location.protocol === 'https:' ? `${window.location.origin}/webhook/${selectedShop.id}` : `https://${window.location.hostname}/webhook/${selectedShop.id}`, 'url')}
                          className="px-3 py-2 rounded-xl bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/40 text-emerald-200 font-medium flex items-center space-x-1 whitespace-nowrap cursor-pointer"
                        >
                          {copiedKey === 'url' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{copiedKey === 'url' ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Meta servers strictly require <b>HTTPS</b>. If running on Oracle Cloud IP (<code>{window.location.hostname}</code>), link your free DuckDNS domain with Certbot SSL (e.g. <code>https://myprintbridge.duckdns.org/webhook/{selectedShop.id}</code>) or use an active ngrok/Cloudflare tunnel.
                      </p>
                    </div>

                    {/* Verify Token */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-400 text-[11px]">Verify Token (Enter in Meta Developer Console):</label>
                        <span className="text-[10px] text-emerald-400 font-mono">✓ Active & Valid</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          readOnly
                          value={selectedShop.whatsapp_verify_token || 'print_shop_secret_2026'}
                          className="w-full px-3 py-2 bg-slate-950 border border-white/10 rounded-xl text-slate-200 font-mono text-[11px]"
                        />
                        <button
                          type="button"
                          onClick={() => handleCopy(selectedShop.whatsapp_verify_token || 'print_shop_secret_2026', 'token')}
                          className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center space-x-1 whitespace-nowrap cursor-pointer"
                        >
                          {copiedKey === 'token' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{copiedKey === 'token' ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-white/5 border border-white/5 text-[11px] text-slate-300 space-y-1">
                    <div className="font-semibold text-white">Step-by-step Meta Console instructions:</div>
                    <ol className="list-decimal list-inside space-y-1 text-slate-400">
                      <li>Go to <b>developers.facebook.com</b> &gt; WhatsApp &gt; <b>Configuration</b>.</li>
                      <li>In <b>Webhook</b> section, click <b>Edit</b>, paste the Callback URL and Verify Token above, and click <b>Verify and save</b>.</li>
                      <li>Click <b>Manage</b> under Webhook fields, find <b>messages</b>, and click <b>Subscribe</b>.</li>
                    </ol>
                  </div>
                </div>

                {/* 3. Enter/Replace WhatsApp Credentials */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                  <h4 className="font-bold text-white text-sm">Update WhatsApp Cloud API Credentials</h4>
                  <p className="text-slate-400 text-xs">
                    Credentials are saved ONLY encrypted (AES-256-GCM) in the database vault and never returned in plaintext.
                  </p>

                  <div className="space-y-2">
                    <div>
                      <label className="text-slate-400 text-[11px]">Phone Number ID (from Meta API Setup)</label>
                      <input
                        type="text"
                        name="wa_phone_number_id_field"
                        autoComplete="off"
                        placeholder="e.g. 102938475610293"
                        value={waCreds.phoneNumberId}
                        onChange={(e) => setWaCreds({ ...waCreds, phoneNumberId: e.target.value })}
                        className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="text-slate-400 text-[11px]">WABA ID (WhatsApp Business Account ID)</label>
                      <input
                        type="text"
                        name="wa_waba_id_field"
                        autoComplete="off"
                        placeholder="e.g. 192837465019283"
                        value={waCreds.wabaId}
                        onChange={(e) => setWaCreds({ ...waCreds, wabaId: e.target.value })}
                        className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="text-slate-400 text-[11px]">Meta App Secret (from App Basic Settings)</label>
                      <input
                        type="password"
                        name="wa_app_secret_field"
                        autoComplete="new-password"
                        placeholder="32-character Meta App Secret"
                        value={waCreds.appSecret}
                        onChange={(e) => setWaCreds({ ...waCreds, appSecret: e.target.value })}
                        className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="text-slate-400 text-[11px]">Verify Token (custom secret for Meta challenge)</label>
                      <input
                        type="text"
                        name="wa_verify_token_field"
                        autoComplete="off"
                        placeholder={`e.g. pb_verify_${selectedShop.id.slice(0, 8)}`}
                        value={waCreds.verifyToken}
                        onChange={(e) => setWaCreds({ ...waCreds, verifyToken: e.target.value })}
                        className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="text-slate-400 text-[11px]">Permanent Access Token (System User with whatsapp_business_messaging)</label>
                      <input
                        type="password"
                        name="wa_access_token_field"
                        autoComplete="new-password"
                        placeholder="EAABw..."
                        value={waCreds.accessToken}
                        onChange={(e) => setWaCreds({ ...waCreds, accessToken: e.target.value })}
                        className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white focus:outline-none focus:border-brand-500"
                      />
                    </div>
                  </div>

                  {waSaveResult && (
                    <div className={`p-3 rounded-xl border flex items-start space-x-2 ${
                      waSaveResult.saved && waSaveResult.verified
                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                        : 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                    }`}>
                      {waSaveResult.saved && waSaveResult.verified ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
                      )}
                      <div>
                        <div className="font-bold">
                          {waSaveResult.saved && waSaveResult.verified
                            ? 'Credentials Saved & Verified with Meta Graph API!'
                            : waSaveResult.saved
                            ? 'Credentials Saved in Vault (Warning: Meta verification returned an error)'
                            : 'Failed to Save Credentials'}
                        </div>
                        <div className="text-[11px] mt-0.5">
                          {waSaveResult.testResult?.error || waSaveResult.error || 'WhatsApp vault credentials updated successfully.'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3 pt-2">
                    <button
                      onClick={() => handleSaveWhatsApp(selectedShop.id)}
                      disabled={submitting || !waCreds.phoneNumberId || !waCreds.accessToken}
                      className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold flex items-center space-x-2 transition-all disabled:opacity-50"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      <span>{submitting ? 'Saving & Verifying...' : 'Save & Encrypt Credentials'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTestWhatsApp(selectedShop.id, waCreds)}
                      disabled={testingWa || !waCreds.phoneNumberId || !waCreds.accessToken}
                      className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium flex items-center space-x-1.5 transition-colors disabled:opacity-50"
                    >
                      <Activity className="w-4 h-4" />
                      <span>{testingWa ? 'Testing with Meta...' : 'Test Entered Credentials'}</span>
                    </button>
                  </div>
                </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Message Switches */}
            {activeTab === 'switches' && (
              <div className="space-y-4 text-xs">
                {/* Master Switch Banner / Card */}
                {(() => {
                  const isAdminAllowed = selectedShop.features?.adminMessagingAllowed !== false;
                  const isMasterOn = selectedShop.features?.allMessagesEnabled !== false;

                  return (
                    <div className={`p-4 rounded-2xl border transition-all ${
                      isAdminAllowed 
                        ? 'bg-emerald-500/10 border-emerald-500/30 shadow-lg shadow-emerald-500/5' 
                        : 'bg-rose-500/10 border-rose-500/30 shadow-lg shadow-rose-500/5'
                    }`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className={`w-2.5 h-2.5 rounded-full animate-pulse ${isAdminAllowed ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                            <h4 className="font-bold text-sm text-white uppercase tracking-wide">
                              Master WhatsApp Switch
                            </h4>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase ${
                              isAdminAllowed 
                                ? (isMasterOn ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')
                                : 'bg-rose-500/20 text-rose-300'
                            }`}>
                              {isAdminAllowed ? (isMasterOn ? 'ALL MESSAGES ON' : 'SHOP MUTED') : 'LOCKED BY ADMIN (OFF)'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-300 leading-relaxed">
                            {isAdminAllowed ? (
                              <span>Shop is actively allowed to send outbound automated WhatsApp messages to customers. Counter staff can turn on or off from shop terminal.</span>
                            ) : (
                              <span className="text-rose-300 font-medium">100% Pure Inbound / Silent Mode active! Locked by Admin: Counter staff CANNOT turn on or send messages. Zero messages will be sent to customers under any circumstance (0% WhatsApp ban risk). Files will be downloaded and printed completely silently.</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            const newStatus = !isAdminAllowed;
                            const updated = { 
                              ...selectedShop.features, 
                              adminMessagingAllowed: newStatus,
                              allMessagesEnabled: newStatus 
                            };
                            await api.updateShop(selectedShop.id, { features: updated });
                            await loadShops();
                            setSelectedShop(await api.getShop(selectedShop.id));
                          }}
                          className={`px-4 py-2 rounded-xl text-xs font-black tracking-wider transition-all shadow-md shrink-0 ${
                            isAdminAllowed
                              ? 'bg-rose-500 hover:bg-rose-400 text-white'
                              : 'bg-emerald-500 hover:bg-emerald-400 text-black'
                          }`}
                        >
                          {isAdminAllowed ? 'TURN OFF ALL' : 'TURN ON ALL'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                <div className="pt-2">
                  <p className="text-slate-400 font-medium mb-3">
                    Individual WhatsApp Message Controls (Configure each message type):
                  </p>

                  <div className="space-y-2.5">
                    {[
                      {
                        key: 'welcomeMessageEnabled',
                        label: '1. Welcome & Guidance Greeting',
                        desc: 'Replies when customer sends hi, hello, or text with instructions on how to send PDFs or files.'
                      },
                      {
                        key: 'orderReceivedEnabled',
                        label: '2. Order Received Confirmation',
                        desc: 'Immediate reply with queue number, file count, and cost estimate when documents arrive.'
                      },
                      {
                        key: 'printingConfirmationEnabled',
                        label: '3. Printing Done Confirmation',
                        desc: 'Notifies customer that their print job has completed and is ready for pickup at the counter.'
                      },
                      {
                        key: 'orderRevivedNoticeEnabled',
                        label: '4. Order Restored / Revived Notice',
                        desc: 'Notifies customer when their skipped or archived order is revived back into the active queue.'
                      },
                      {
                        key: 'turnNextEnabled',
                        label: '5. Missed Turn / Line Skip Alert',
                        desc: 'Alerts customer when shopkeeper clicks Next Turn or skips their position in the queue.'
                      },
                      {
                        key: 'expiryNoticeEnabled',
                        label: '6. 23-Hour Inactivity Expiry Notice',
                        desc: 'Notice sent to customer before untouched files are cleared from the queue for privacy.'
                      },
                    ].map((sw) => {
                      const isAdminAllowed = selectedShop.features?.adminMessagingAllowed !== false;
                      const isMasterOn = selectedShop.features?.allMessagesEnabled !== false && isAdminAllowed;
                      const isOn = selectedShop.features?.[sw.key] !== false;

                      return (
                        <div
                          key={sw.key}
                          className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all ${
                            !isAdminAllowed
                              ? 'bg-white/[0.02] border-white/5 opacity-50'
                              : !isMasterOn
                              ? 'bg-white/[0.02] border-white/5 opacity-60'
                              : isOn
                              ? 'bg-white/5 border-white/10 hover:border-white/20'
                              : 'bg-black/30 border-white/5'
                          }`}
                        >
                          <div className="space-y-0.5 pr-4">
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold text-white">{sw.label}</span>
                              {!isAdminAllowed ? (
                                <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-rose-500/20 text-rose-300">
                                  LOCKED BY ADMIN
                                </span>
                              ) : !isMasterOn && (
                                <span className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-amber-500/20 text-amber-300">
                                  MUTED BY MASTER
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-400">{sw.desc}</p>
                          </div>
                          <button
                            onClick={async () => {
                              const updated = { ...selectedShop.features, [sw.key]: !isOn };
                              await api.updateShop(selectedShop.id, { features: updated });
                              await loadShops();
                              setSelectedShop(await api.getShop(selectedShop.id));
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 ${
                              isOn
                                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                                : 'bg-slate-700/60 text-slate-400 hover:bg-slate-700'
                            }`}
                          >
                            {isOn ? 'ENABLED (ON)' : 'DISABLED (OFF)'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Access & Shopkeeper Credentials */}
            {activeTab === 'access' && (
              <div className="space-y-4 text-xs">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm flex items-center space-x-2">
                        <Lock className="w-4 h-4 text-emerald-400" />
                        <span>Shopkeeper Terminal Login Credentials</span>
                      </h4>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Manage the shopkeeper username and password used at the counter terminal.
                      </p>
                    </div>
                    {loadingShopkeeperCreds && (
                      <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" />
                    )}
                  </div>

                  {shopkeeperCreds ? (
                    <div className="space-y-3 pt-2">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="text-slate-400 text-[11px] block mb-1">Current Shopkeeper Username</label>
                          <input
                            type="text"
                            value={editShopkeeperUsername}
                            onChange={(e) => setEditShopkeeperUsername(e.target.value)}
                            className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="text-slate-400 text-[11px] block mb-1">
                            Shopkeeper Email <span className="text-emerald-400 font-normal">(For OTPs)</span>
                          </label>
                          <input
                            type="email"
                            placeholder="e.g. shop@example.com"
                            value={editShopkeeperEmail}
                            onChange={(e) => setEditShopkeeperEmail(e.target.value)}
                            className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white"
                          />
                        </div>
                        <div>
                          <label className="text-slate-400 text-[11px] block mb-1">Set New Password (Leave blank to keep)</label>
                          <input
                            type="password"
                            placeholder="••••••••••••"
                            value={editShopkeeperPassword}
                            onChange={(e) => setEditShopkeeperPassword(e.target.value)}
                            className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white"
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        💡 If email is provided, browser sign-in OTP PINs will be sent directly to this shopkeeper email. If left blank, OTPs will route to your Admin email.
                      </p>

                      {shopkeeperUpdateStatus && (
                        <div className={`p-3 rounded-xl border flex items-center space-x-2 ${
                          shopkeeperUpdateStatus.ok
                            ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                            : 'bg-rose-500/20 border-rose-500/40 text-rose-300'
                        }`}>
                          {shopkeeperUpdateStatus.ok ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-rose-400" />
                          )}
                          <span>{shopkeeperUpdateStatus.msg}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleSaveShopkeeperCreds(selectedShop.id)}
                        disabled={savingShopkeeperCreds}
                        className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center space-x-1.5 transition disabled:opacity-50 cursor-pointer"
                      >
                        {savingShopkeeperCreds ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        <span>Save Shopkeeper Credentials</span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-slate-400 py-3">
                      Loading shopkeeper credentials or no user linked.
                    </div>
                  )}
                </div>

                {/* Dedicated Shop IP Policy / Network Lock */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm flex items-center space-x-2">
                        <Wifi className="w-4 h-4 text-brand-400" />
                        <span>Dedicated Shop IP & Network Lock</span>
                      </h4>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Restrict this shopkeeper to specific IP addresses or subnets (e.g. broadband router static IP).
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-1">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="modal_ip_lock"
                        checked={shopIpLock.ipLockEnabled}
                        onChange={(e) => setShopIpLock({ ...shopIpLock, ipLockEnabled: e.target.checked })}
                        className="w-4 h-4 rounded text-brand-600 bg-slate-900 border-white/20 focus:ring-brand-500 cursor-pointer"
                      />
                      <label htmlFor="modal_ip_lock" className="text-slate-200 font-semibold cursor-pointer">
                        Enable Dedicated IP Lock for this Shop
                      </label>
                    </div>

                    {shopIpLock.ipLockEnabled && (
                      <div>
                        <label className="text-slate-400 text-[11px] block mb-1">
                          Allowed IP Addresses (comma-separated, e.g. 192.168.1.50, 103.21.5.12)
                        </label>
                        <input
                          type="text"
                          value={shopIpLock.allowedIps}
                          onChange={(e) => setShopIpLock({ ...shopIpLock, allowedIps: e.target.value })}
                          placeholder="e.g. 192.168.1.50, 103.21.5.12"
                          className="w-full px-3.5 py-2 bg-slate-900 border border-white/10 rounded-xl text-white font-mono"
                        />
                      </div>
                    )}

                    {ipLockStatus && (
                      <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center space-x-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>{ipLockStatus}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleSaveIpLock(selectedShop.id)}
                      disabled={savingIpLock}
                      className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold flex items-center space-x-1.5 transition disabled:opacity-50 cursor-pointer"
                    >
                      {savingIpLock ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                      <span>Update IP Network Policy</span>
                    </button>
                  </div>
                </div>

                {/* Authorized Browser Terminals for this Shop */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm flex items-center space-x-2">
                        <Laptop className="w-4 h-4 text-emerald-400" />
                        <span>Authorized Browser Hardware Terminals</span>
                      </h4>
                      <p className="text-slate-400 text-xs mt-0.5">
                        Each browser that logs into this shopkeeper account generates a unique cryptographic device ID. Only approved browsers can open this counter terminal.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => loadShopModalDevices(selectedShop.id)}
                      className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold flex items-center space-x-1 transition cursor-pointer"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${loadingShopModalDevices ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </div>

                  {shopModalDevices.length === 0 ? (
                    <div className="p-4 rounded-xl bg-slate-900/60 border border-white/5 text-center space-y-2">
                      <div className="text-slate-400 text-xs">
                        {loadingShopModalDevices
                          ? 'Checking registered browser devices...'
                          : 'No browser hardware devices have connected to this shop yet.'}
                      </div>
                      <p className="text-[11px] text-slate-500 max-w-md mx-auto">
                        To connect: Have the shopkeeper sign in on the counter PC at <code>/shop</code> with their username & password. Their browser token will appear here as <strong>PENDING</strong> for 1-click approval.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 pt-1">
                      {shopModalDevices.map((dev) => (
                        <div
                          key={dev.id}
                          className="p-3 rounded-xl bg-slate-900/80 border border-white/10 flex flex-wrap items-center justify-between gap-3"
                        >
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold text-white text-xs">{dev.label || 'Web Browser'}</span>
                              <span
                                className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                  dev.approved
                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse'
                                }`}
                              >
                                {dev.approved ? 'APPROVED' : 'PENDING APPROVAL'}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                              IP: {dev.last_ip || 'Local Network'} • Last Seen:{' '}
                              {dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleTimeString() : 'Never'}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {dev.approved ? (
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleShopModalDeviceApproval(dev.id, false, selectedShop.id)
                                }
                                className="px-3 py-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-800/40 text-xs font-semibold transition cursor-pointer"
                              >
                                Revoke Browser
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleShopModalDeviceApproval(dev.id, true, selectedShop.id)
                                }
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-md shadow-emerald-600/30 transition cursor-pointer"
                              >
                                Approve Browser
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteShopModalDevice(dev.id, selectedShop.id)}
                              title="Delete Browser Approval"
                              className="px-2.5 py-1.5 rounded-lg bg-red-950/30 hover:bg-red-900/50 text-red-400 hover:text-red-200 border border-red-800/40 text-xs font-semibold transition cursor-pointer flex items-center space-x-1"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cross-Shop Isolation Guarantee */}
                <div className="p-4 rounded-2xl bg-indigo-950/40 border border-indigo-500/20 space-y-2">
                  <div className="flex items-center space-x-2 text-indigo-300 font-bold text-sm">
                    <ShieldCheck className="w-4 h-4 text-indigo-400" />
                    <span>Cross-Shop Device & Network Isolation Guarantee</span>
                  </div>
                  <p className="text-slate-300 text-xs leading-relaxed">
                    <strong>Why Shopkeeper A Cannot Open Shop B:</strong> Every approved browser device is tied exclusively to that shop's user ID. Even if Shopkeeper A discovers the username and password of Shopkeeper B, Shopkeeper A's counter browser device is <strong>unauthorized for Shop B</strong>. The server will immediately block them with <code>403 DEVICE_UNAPPROVED</code>.
                  </p>
                  <p className="text-slate-400 text-[11px] leading-relaxed">
                    When Dedicated IP Lock is also enabled, Shop B will additionally reject requests coming from Shop A's IP address.
                  </p>
                </div>

                {/* College / Shared WiFi Zero-Trust Security Explainer */}
                <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/20 space-y-2">
                  <div className="flex items-center space-x-2 text-emerald-300 font-bold text-sm">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    <span>College WiFi & Shared Network Security Shield</span>
                  </div>
                  <p className="text-slate-300 text-xs leading-relaxed">
                    <strong>Why Students Cannot Log In on the Same WiFi:</strong> In a college or campus environment, all student phones and laptops share the same public IP. To prevent students from accessing the shopkeeper terminal, access is strictly locked to <strong>Approved Browser Hardware Devices</strong>.
                  </p>
                  <p className="text-slate-400 text-[11px] leading-relaxed">
                    Even if a student enters the shopkeeper's username and password from their phone or laptop, their browser is rejected with <code>403 DEVICE_UNAPPROVED</code>. Only the physical counter PC you approve in the <strong>Browser Approvals</strong> tab is granted entry.
                  </p>
                </div>
              </div>
            )}

            {/* Tab: Agent Controls */}
            {activeTab === 'agent' && (
              <div className="space-y-4 text-xs">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Agent Connectivity:</span>
                    <span className={selectedShop.agent_online ? 'text-emerald-400 font-bold' : 'text-slate-500 font-bold'}>
                      {selectedShop.agent_online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Last Seen:</span>
                    <span className="text-slate-300">
                      {selectedShop.agent_last_seen ? new Date(selectedShop.agent_last_seen).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>

                {/* 1-Click Agent Package Download */}
                <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-slate-900/90 to-brand-500/10 border border-emerald-500/30 space-y-3">
                  <div className="font-bold text-white text-sm flex items-center space-x-2">
                    <Laptop className="w-4 h-4 text-emerald-400" />
                    <span>Download Pre-configured Shop Agent</span>
                  </div>
                  <p className="text-slate-400 text-xs">
                    Downloads a ready-to-run ZIP containing <code>START_AGENT.bat</code> and <code>config.json</code> already configured with this shop's ID and key. Double-click to start anytime!
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.downloadAgentZip(selectedShop.id, selectedShop.name);
                      } catch (err: any) {
                        alert(`Could not download agent: ${err.message}`);
                      }
                    }}
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md shadow-emerald-600/30 transition cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Agent (.zip) for {selectedShop.name}</span>
                  </button>
                </div>

                <div className="pt-2 flex items-center justify-between">
                  <button
                    onClick={async () => {
                      if (confirm('Regenerate agent key? The shop PC will stop working until the new key is entered.')) {
                        const res = await api.regenerateAgentKey(selectedShop.id);
                        alert(`New Agent Key: ${res.agentKey}\nStore this key now!`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 font-semibold cursor-pointer transition"
                  >
                    Regenerate Agent Key
                  </button>
                </div>
              </div>
            )}

            {/* Tab: Danger Zone */}
            {activeTab === 'danger' && (
              <div className="space-y-4 text-xs">
                <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 space-y-2">
                  <div className="font-bold text-sm">Delete Shop Permanently</div>
                  <p>
                    This will delete all orders, files, and customer records for this shop. Type <strong>{selectedShop.name}</strong> to confirm.
                  </p>
                </div>

                <button
                  onClick={async () => {
                    const confirmName = prompt(`Type "${selectedShop.name}" to permanently delete this shop:`);
                    if (confirmName) {
                      await api.deleteShop(selectedShop.id, confirmName);
                      setSelectedShop(null);
                      await loadShops();
                    }
                  }}
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-semibold"
                >
                  Delete Shop
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
